import { Button, Col, Row, Typography } from "antd";
import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import styled, { css, keyframes } from "styled-components";
import { ShadowBoxCard } from "./card";

const { Title, Text } = Typography;

const badgePop = keyframes`
  0% {
    transform: scale(0.6);
    opacity: 0;
  }
  60% {
    transform: scale(1.08);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const checkDraw = keyframes`
  from {
    stroke-dashoffset: 60;
  }
  to {
    stroke-dashoffset: 0;
  }
`;

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/**
 * Posle zakazivanja na stranici ostane samo kartica potvrde, pa je sadržaj
 * mnogo niži od ekrana: kartica visi uz vrh, a ispod nje zjapi prazan prostor
 * do futera. Sekcija zauzme tu preostalu visinu i centrira karticu u njoj.
 *
 * Oduzima se stvarna visina footera (`--footer-height`, koju footer objavljuje i
 * menja pri promeni sadržaja ili skraćenom footeru) plus vertikalni padding
 * `AppContent` (48px) i 8px rezerve. Namerno je malo manja od stvarne: ako
 * promaši naniže kartica samo sedne nešto iznad sredine, a ako bi promašila
 * naviše stranica bi dobila suvišan skrol. Na telefonu kartica ionako popunjava
 * ekran, pa centriranje nema šta da doda.
 */
const SuccessSection = styled(Row)<{ $compact?: boolean }>`
  min-height: calc(
    100svh - var(--header-height) - var(--footer-height, 400px) - 56px
  );
  align-items: center;

  @media (max-width: 768px) {
    min-height: 0;
  }

  /* Kompaktna potvrda ima mnogo sadržaja, pa ne centrira: stoji uz vrh, bez praznog razmaka iznad */
  ${(p) =>
    p.$compact &&
    css`
      min-height: 0;
      align-items: flex-start;
    `}
`;

const SuccessWrap = styled.div<{ $compact?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: ${(p) => (p.$compact ? "4px 16px 16px" : "32px 24px 24px")};
`;

const SuccessBadge = styled.div<{ $compact?: boolean; $error?: boolean }>`
  width: ${(p) => (p.$compact ? "56px" : "96px")};
  height: ${(p) => (p.$compact ? "56px" : "96px")};
  border-radius: 50%;
  background: ${(p) =>
    p.$error
      ? "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)"
      : "var(--color-gradient)"};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${(p) =>
    p.$error ? "0 8px 24px rgba(185, 28, 28, 0.3)" : "var(--shadow-primary-lg)"};
  margin-bottom: ${(p) => (p.$compact ? "12px" : "24px")};
  animation: ${badgePop} 480ms var(--ease-out) both;

  svg {
    width: ${(p) => (p.$compact ? "26px" : "44px")};
    height: ${(p) => (p.$compact ? "26px" : "44px")};
    color: #fff;
  }

  svg path {
    stroke: #fff;
    stroke-width: 3.5;
    stroke-linecap: round;
    stroke-linejoin: round;
    fill: none;
    stroke-dasharray: 60;
    stroke-dashoffset: 60;
    animation: ${checkDraw} 360ms 240ms var(--ease-out) forwards;
  }
`;

const SuccessTitle = styled(Title)`
  &.ant-typography {
    font-size: clamp(1.125rem, 4.5vw, 1.5rem);
    margin: 0 0 8px;
    color: var(--color-text);
    /*
      Tekst dolazi iz prevoda i razlikuje se po jeziku i po tome da li je termin
      potvrđen ili je zahtev tek poslat. Sa nowrap je engleski naslov izlazio iz
      kartice i pravio horizontalni skrol na telefonu.
    */
    text-wrap: balance;
    animation: ${fadeUp} 420ms 120ms var(--ease-out) both;
  }
`;

const SuccessSubtitle = styled(Text)`
  &.ant-typography {
    color: var(--color-text);
    font-size: 1.0625rem;
    font-weight: var(--weight-medium);
    max-width: 360px;
    line-height: var(--leading-relaxed);
    animation: ${fadeUp} 420ms 200ms var(--ease-out) both;
  }
`;

const SuccessHelper = styled(Text)`
  &.ant-typography {
    color: var(--color-text-tertiary);
    font-size: 0.8125rem;
    max-width: 360px;
    line-height: var(--leading-normal);
    margin-top: 6px;
    animation: ${fadeUp} 420ms 260ms var(--ease-out) both;
  }
`;

const SuccessExtra = styled.div`
  margin-top: 20px;
  width: 100%;
  max-width: 320px;
  animation: ${fadeUp} 420ms 300ms var(--ease-out) both;
`;

/** Glavno dugme ustupa drugom koraku; ostaje dostupno, ali bez da se bori za pažnju. */
const SuccessQuietAction = styled(Button)`
  &.ant-btn {
    margin-top: 12px;
    animation: ${fadeUp} 420ms 340ms var(--ease-out) both;
  }
`;

const SuccessPrimaryAction = styled(Button)`
  &.ant-btn {
    margin-top: 24px;
    height: 44px;
    padding: 0 28px;
    border-radius: var(--radius-md);
    font-weight: var(--weight-semibold);
    animation: ${fadeUp} 420ms 340ms var(--ease-out) both;
  }
`;

const SuccessSecondaryLink = styled(Link)`
  margin-top: 16px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--color-text-secondary);
  font-size: 0.875rem;
  font-weight: var(--weight-medium);
  text-decoration: none;
  animation: ${fadeUp} 420ms 420ms var(--ease-out) both;
  transition: color var(--duration-normal) var(--ease-out);

  .label {
    text-decoration: underline;
    text-decoration-color: var(--color-border);
    text-underline-offset: 4px;
    text-decoration-thickness: 1.5px;
    transition: text-decoration-color var(--duration-normal) var(--ease-out);
  }

  .arrow {
    transition: transform var(--duration-normal) var(--ease-out);
  }

  &:hover {
    color: var(--color-primary);

    .label {
      text-decoration-color: var(--color-primary);
    }

    .arrow {
      transform: translateX(3px);
    }
  }
`;

interface Action {
  label: string;
  /** Ili `to` (navigacija), ili `onClick`. */
  to?: string;
  onClick?: () => void;
  /** Tih link umesto glavnog dugmeta, kad na ekranu postoji važniji sledeći korak. */
  quiet?: boolean;
}

interface Props {
  /** `error` za neuspeh (npr. link za aktivaciju više ne važi): isti izgled, crvena značka sa X. */
  status?: "success" | "error";
  title: string;
  /** Izostavlja se kad ga zamenjuje sadržaj ispod (npr. sažetak termina). */
  description?: string;
  /** Manja značka i uži razmaci, za ekran sa puno sadržaja koji treba da stane bez skrola. */
  compact?: boolean;
  /** Sitniji pomoćni red ispod opisa. */
  helper?: string;
  /** Dodatan sadržaj između opisa i dugmadi (npr. dodavanje u kalendar). */
  children?: React.ReactNode;
  primary?: Action;
  /** Tiši link ispod glavnog dugmeta. */
  secondary?: { label: string; to: string };
}

/**
 * Ekran ishoda posle slanja forme (rezervacija, prijava partnera, registracija,
 * aktivacija naloga). Jedan izgled za sve, da potvrde ne izgledaju kao da su iz
 * različitih aplikacija; `status="error"` za neuspeh.
 */
export const SuccessCard: React.FC<Props> = ({
  status = "success",
  title,
  description,
  compact,
  helper,
  children,
  primary,
  secondary,
}) => {
  const navigate = useNavigate();
  const isError = status === "error";

  useEffect(() => {
    // Sa dna duge forme `smooth` skrol traje i po sekund, pa bi se kartica
    // potvrde pojavila dok je stranica još u pola ekrana.
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  return (
    <SuccessSection justify="center" $compact={compact}>
      <Col xs={24} md={16} lg={12} xl={10}>
        <ShadowBoxCard>
          {/* Zamena ekrana nije navigacija, pa čitač ekrana ovako sazna ishod. */}
          <SuccessWrap role={isError ? "alert" : "status"} $compact={compact}>
            <SuccessBadge aria-hidden="true" $compact={compact} $error={isError}>
              <svg viewBox="0 0 24 24">
                <path
                  d={isError ? "M7 7 L17 17 M17 7 L7 17" : "M5 12.5 L10 17.5 L19 7.5"}
                />
              </svg>
            </SuccessBadge>
            <SuccessTitle level={2}>{title}</SuccessTitle>
            {description && <SuccessSubtitle>{description}</SuccessSubtitle>}
            {helper && <SuccessHelper>{helper}</SuccessHelper>}
            {children && <SuccessExtra>{children}</SuccessExtra>}
            {primary &&
              (primary.quiet ? (
                <SuccessQuietAction
                  type="link"
                  onClick={() =>
                    primary.to ? navigate(primary.to) : primary.onClick?.()
                  }
                >
                  {primary.label}
                </SuccessQuietAction>
              ) : (
                <SuccessPrimaryAction
                  type="primary"
                  onClick={() =>
                    primary.to ? navigate(primary.to) : primary.onClick?.()
                  }
                >
                  {primary.label}
                </SuccessPrimaryAction>
              ))}
            {secondary && (
              <SuccessSecondaryLink to={secondary.to}>
                <span className="label">{secondary.label}</span>
                <span className="arrow" aria-hidden="true">
                  →
                </span>
              </SuccessSecondaryLink>
            )}
          </SuccessWrap>
        </ShadowBoxCard>
      </Col>
    </SuccessSection>
  );
};
